import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  ActivityIndicator,
  useWindowDimensions,
  Alert,
} from 'react-native';
import { Image } from 'expo-image';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import { useUserPreferences } from '../context/UserPreferencesContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import RecordCellEditor from '../components/RecordCellEditor';
import RecordRelatedLists, { relatedSelectionKey } from '../components/RecordRelatedLists';
import RecordRelatedBulkEditModal from '../components/RecordRelatedBulkEditModal';
import { getApiUrl } from '../utils/apiConfig';
import { hasSuperUserAccess } from '../utils/sessionUser';
import { LAYOUT_PRESETS, normalizeLayout, CORE_FIELDS_LIMIT } from '../utils/recordPageLayoutModel';
import {
  formatRecordFieldValue,
  resolveRecordTitle,
  fieldLabelFor,
  buildFallbackDetailFields,
  getCoreObjectFields,
  HIDDEN_DETAIL_FIELDS,
} from '../utils/recordFieldDisplay';

function valuesEqual(a, b) {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return Boolean(a) === Boolean(b);
  }
  return String(a ?? '') === String(b ?? '');
}

function isFieldEditable(fieldName, fieldMetaByName, objectApiName) {
  if (!fieldName || fieldName === 'id') return false;
  if (HIDDEN_DETAIL_FIELDS.has(fieldName)) return false;
  if (objectApiName === 'user_breaks' && fieldName === 'video_link') return true;
  return fieldMetaByName[fieldName]?.is_editable === true;
}

function EditableFieldRow({
  label,
  fieldName,
  record,
  fieldMetaByName,
  objectApiName,
  isEditing,
  onFieldChange,
  isUSUser,
  theme,
  styles,
  isWide,
  lookupLinks,
  onOpenRelatedRecord,
}) {
  const fieldMeta = fieldMetaByName[fieldName];
  const value = record?.[fieldName];
  const editable = isEditing && isFieldEditable(fieldName, fieldMetaByName, objectApiName);
  const lookup = lookupLinks?.[fieldName];

  return (
    <View
      style={[styles.fieldRow, isWide && styles.fieldRowWide, editable && styles.fieldRowEditing]}
      {...(Platform.OS === 'web' ? { className: 'fieldRow' } : {})}
    >
      <Text style={styles.fieldLabel}>{label}</Text>
      {editable ? (
        <View style={styles.fieldEditorWrap}>
          <RecordCellEditor
            value={value}
            fieldMeta={fieldMeta}
            theme={theme}
            isUSUser={isUSUser}
            onChange={(next) => onFieldChange(fieldName, next)}
          />
        </View>
      ) : lookup && onOpenRelatedRecord ? (
        <TouchableOpacity
          onPress={() =>
            onOpenRelatedRecord({
              object_api_name: lookup.related_object_api_name,
              object_label: lookup.related_object_label,
              id: lookup.related_record_id,
              title: lookup.related_record_title,
            })
          }
          activeOpacity={0.7}
          {...(Platform.OS === 'web' ? { dataSet: { recordDetailLookupLink: 'true' } } : {})}
        >
          <Text style={styles.lookupLinkText}>{lookup.related_record_title}</Text>
        </TouchableOpacity>
      ) : (
        <Text style={styles.fieldValue} selectable>
          {formatRecordFieldValue(value, fieldMeta, isUSUser)}
        </Text>
      )}
    </View>
  );
}

function SectionCard({ title, children, theme, styles, isEditing = false }) {
  return (
    <View
      style={[styles.sectionCard, isEditing && styles.sectionCardEditing]}
      {...(Platform.OS === 'web' ? { dataSet: { recordDetailSection: 'true' } } : {})}
    >
      {title ? (
        <View style={styles.sectionHeader}>
          <View style={styles.sectionAccent} />
          <Text style={styles.sectionTitle}>{title}</Text>
        </View>
      ) : null}
      <View style={styles.sectionBody}>{children}</View>
    </View>
  );
}

function renderColumnWidgets(widgets, ctx) {
  const {
    record,
    fieldMetaByName,
    objectApiName,
    objectFieldMeta,
    isUSUser,
    theme,
    styles,
    isWide,
    isEditing,
    onFieldChange,
    lookupLinks,
    onOpenRelatedRecord,
  } = ctx;

  const elements = [];
  let currentSectionTitle = null;
  let pendingFields = [];

  const flushFields = () => {
    if (pendingFields.length === 0) return;
    elements.push(
      <SectionCard
        key={`section-${elements.length}-${currentSectionTitle || 'fields'}`}
        title={currentSectionTitle}
        theme={theme}
        styles={styles}
        isEditing={isEditing}
      >
        {pendingFields.map(({ fieldName, label }) => (
          <EditableFieldRow
            key={fieldName}
            label={label || fieldLabelFor(fieldName, fieldMetaByName)}
            fieldName={fieldName}
            record={record}
            fieldMetaByName={fieldMetaByName}
            objectApiName={objectApiName}
            isEditing={isEditing}
            onFieldChange={onFieldChange}
            isUSUser={isUSUser}
            theme={theme}
            styles={styles}
            isWide={isWide}
            lookupLinks={lookupLinks}
            onOpenRelatedRecord={onOpenRelatedRecord}
          />
        ))}
      </SectionCard>
    );
    pendingFields = [];
  };

  for (const widget of widgets) {
    if (widget.type === 'section') {
      flushFields();
      currentSectionTitle = widget.label || 'Section';
      continue;
    }

    if (widget.type === 'field') {
      if (HIDDEN_DETAIL_FIELDS.has(widget.fieldName)) continue;
      pendingFields.push({
        fieldName: widget.fieldName,
        label: widget.label || fieldLabelFor(widget.fieldName, fieldMetaByName),
      });
      continue;
    }

    flushFields();
    currentSectionTitle = null;

    if (widget.type === 'core-fields') {
      const coreFields = getCoreObjectFields(objectFieldMeta, CORE_FIELDS_LIMIT);
      elements.push(
        <SectionCard
          key={widget.id}
          title={widget.label || 'Core fields'}
          theme={theme}
          styles={styles}
          isEditing={isEditing}
        >
          {coreFields.length === 0 ? (
            <Text style={styles.sectionPlaceholder}>No fields available</Text>
          ) : (
            coreFields.map(({ fieldName, label }) => (
              <EditableFieldRow
                key={fieldName}
                label={label || fieldLabelFor(fieldName, fieldMetaByName)}
                fieldName={fieldName}
                record={record}
                fieldMetaByName={fieldMetaByName}
                objectApiName={objectApiName}
                isEditing={isEditing}
                onFieldChange={onFieldChange}
                isUSUser={isUSUser}
                theme={theme}
                styles={styles}
                isWide={isWide}
                lookupLinks={lookupLinks}
                onOpenRelatedRecord={onOpenRelatedRecord}
              />
            ))
          )}
        </SectionCard>
      );
      continue;
    }

    if (widget.type === 'text') {
      elements.push(
        <View key={widget.id} style={styles.textBlock}>
          <Text style={styles.textBlockContent}>{widget.content || ''}</Text>
        </View>
      );
    } else if (widget.type === 'image') {
      elements.push(
        <View key={widget.id} style={styles.imageBlock}>
          {widget.imageUrl ? (
            <Image
              source={{ uri: widget.imageUrl }}
              style={styles.image}
              contentFit="contain"
            />
          ) : (
            <Text style={styles.imagePlaceholder}>No image URL</Text>
          )}
        </View>
      );
    }
  }

  flushFields();
  return elements;
}

export default function RecordDetailPage({ navigation, route }) {
  const theme = useTheme();
  const { isUSUser } = useUserPreferences();
  const { width } = useWindowDimensions();
  const stopwatchRef = useRef(null);
  const isWide = width >= 768;

  const objectApiName = route?.params?.objectApiName;
  const recordId = route?.params?.recordId;
  const objectLabelParam = route?.params?.objectLabel;

  const [currentUser, setCurrentUser] = useState(route?.params?.user || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [record, setRecord] = useState(null);
  const [objectLabel, setObjectLabel] = useState(objectLabelParam || '');
  const [layout, setLayout] = useState({ rows: [] });
  const [fieldMeta, setFieldMeta] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editBaseline, setEditBaseline] = useState(null);
  const [draftRecord, setDraftRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [relatedData, setRelatedData] = useState(null);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedError, setRelatedError] = useState(null);
  const [selectedRelatedKeys, setSelectedRelatedKeys] = useState([]);
  const [relatedBulkEditOpen, setRelatedBulkEditOpen] = useState(false);
  const [deletingRelated, setDeletingRelated] = useState(false);

  const styles = useMemo(() => getStyles(theme, isWide), [theme, isWide]);

  const fieldMetaByName = useMemo(() => {
    const map = {};
    for (const field of fieldMeta) {
      if (field?.field_name) map[field.field_name] = field;
    }
    if (objectApiName === 'user_breaks' && map.video_link) {
      map.video_link = {
        ...map.video_link,
        is_editable: true,
        input_type: 'json_array',
      };
    }
    return map;
  }, [fieldMeta, objectApiName]);

  const recordTitle = useMemo(
    () => resolveRecordTitle(isEditing ? draftRecord : record, fieldMetaByName),
    [record, draftRecord, isEditing, fieldMetaByName]
  );

  const displayRecord = isEditing ? draftRecord : record;

  const lookupLinks = useMemo(() => {
    const map = {};
    for (const lookup of relatedData?.parent_lookups || []) {
      if (lookup?.field_name) {
        map[lookup.field_name] = lookup;
      }
    }
    return map;
  }, [relatedData]);

  const reloadRelatedData = useCallback(async () => {
    if (!objectApiName || recordId == null) return;
    setRelatedLoading(true);
    setRelatedError(null);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/objects/${encodeURIComponent(objectApiName)}/records/${encodeURIComponent(String(recordId))}/related`
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to load related records');
      }
      setRelatedData(data);
    } catch (err) {
      setRelatedError(err.message || 'Failed to load related records');
      setRelatedData(null);
    } finally {
      setRelatedLoading(false);
    }
  }, [objectApiName, recordId]);

  const selectedRelatedItems = useMemo(() => {
    if (!relatedData || selectedRelatedKeys.length === 0) return [];
    const itemByKey = new Map();
    for (const rel of relatedData.child_relationships || []) {
      for (const item of rel.records || []) {
        itemByKey.set(relatedSelectionKey(item), item);
      }
    }
    return selectedRelatedKeys.map((key) => itemByKey.get(key)).filter(Boolean);
  }, [relatedData, selectedRelatedKeys]);

  const handleToggleRelatedSelect = useCallback((item) => {
    const key = relatedSelectionKey(item);
    setSelectedRelatedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }, []);

  const clearRelatedSelection = useCallback(() => {
    setSelectedRelatedKeys([]);
    setRelatedBulkEditOpen(false);
  }, []);

  const confirmDeleteSelectedRelated = async () => {
    const n = selectedRelatedKeys.length;
    const msg = `Delete ${n} related record${n === 1 ? '' : 's'}? This cannot be undone.`;
    if (Platform.OS === 'web') {
      return window.confirm(msg);
    }
    return new Promise((resolve) => {
      Alert.alert('Delete related records', msg, [
        { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
        { text: 'Delete', style: 'destructive', onPress: () => resolve(true) },
      ]);
    });
  };

  const handleDeleteSelectedRelated = async () => {
    if (!hasSuperUserAccess(currentUser) || selectedRelatedItems.length === 0 || deletingRelated) {
      return;
    }
    const confirmed = await confirmDeleteSelectedRelated();
    if (!confirmed) return;

    const idsByObject = {};
    for (const item of selectedRelatedItems) {
      if (!idsByObject[item.object_api_name]) {
        idsByObject[item.object_api_name] = [];
      }
      idsByObject[item.object_api_name].push(parseInt(String(item.id), 10));
    }

    setDeletingRelated(true);
    try {
      let totalDeleted = 0;
      for (const [relatedObjectApiName, ids] of Object.entries(idsByObject)) {
        const res = await fetch(
          `${getApiUrl()}/api/objects/${encodeURIComponent(relatedObjectApiName)}/records`,
          {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              requester_id: currentUser.id,
              ids,
            }),
          }
        );
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(body.error || `Delete failed (${res.status})`);
        }
        totalDeleted += body.count ?? body.deletedIds?.length ?? ids.length;
      }
      clearRelatedSelection();
      await reloadRelatedData();
      Alert.alert(
        'Deleted',
        `${totalDeleted} related record${totalDeleted === 1 ? '' : 's'} removed.`
      );
    } catch (err) {
      Alert.alert('Delete failed', err.message || 'Could not delete related records.');
    } finally {
      setDeletingRelated(false);
    }
  };

  const handleRelatedBulkEditComplete = useCallback(async () => {
    setRelatedBulkEditOpen(false);
    setSelectedRelatedKeys([]);
    await reloadRelatedData();
  }, [reloadRelatedData]);

  const openRelatedRecord = useCallback(
    (item) => {
      if (!item?.id || !item?.object_api_name) return;
      navigation.push('RecordDetail', {
        objectApiName: item.object_api_name,
        recordId: item.id,
        objectLabel: item.object_label || item.object_api_name,
        user: currentUser,
        userId: currentUser?.id || route?.params?.userId || 1,
      });
    },
    [navigation, currentUser, route?.params?.userId]
  );

  const hasDraftChanges = useMemo(() => {
    if (!isEditing || !editBaseline || !draftRecord) return false;
    for (const [fieldName, meta] of Object.entries(fieldMetaByName)) {
      if (!meta?.is_editable) continue;
      if (!valuesEqual(editBaseline[fieldName], draftRecord[fieldName])) return true;
    }
    return false;
  }, [isEditing, editBaseline, draftRecord, fieldMetaByName]);

  const handleFieldChange = useCallback((fieldName, nextValue) => {
    setDraftRecord((prev) => (prev ? { ...prev, [fieldName]: nextValue } : prev));
    setSaveError(null);
  }, []);

  const startEdit = () => {
    if (!record) return;
    setEditBaseline({ ...record });
    setDraftRecord({ ...record });
    setIsEditing(true);
    setSaveError(null);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditBaseline(null);
    setDraftRecord(null);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!hasSuperUserAccess(currentUser) || !objectApiName || recordId == null) return;
    if (!editBaseline || !draftRecord || !hasDraftChanges) return;

    const fields = {};
    for (const [fieldName, meta] of Object.entries(fieldMetaByName)) {
      if (!meta?.is_editable) continue;
      if (!valuesEqual(editBaseline[fieldName], draftRecord[fieldName])) {
        fields[fieldName] = draftRecord[fieldName];
      }
    }
    if (Object.keys(fields).length === 0) return;

    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/objects/${encodeURIComponent(objectApiName)}/records`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            requester_id: currentUser.id,
            updates: [{ id: parseInt(String(recordId), 10), fields }],
          }),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || `Save failed (${res.status})`);
      }
      const updated = { ...draftRecord };
      setRecord(updated);
      setIsEditing(false);
      setEditBaseline(null);
      setDraftRecord(null);
      Alert.alert('Saved', 'Record updated.');
    } catch (err) {
      setSaveError(err.message || 'Could not save record.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (route?.params?.user) {
      setCurrentUser(route.params.user);
    } else if (route?.params?.userId) {
      fetch(`${getApiUrl()}/api/users/${route.params.userId}`)
        .then((res) => res.json())
        .then((data) => setCurrentUser(data))
        .catch(() => {});
    }
  }, [route]);

  useEffect(() => {
    setIsEditing(false);
    setEditBaseline(null);
    setDraftRecord(null);
    setSaveError(null);
    setActiveTab('details');
    setSelectedRelatedKeys([]);
    setRelatedBulkEditOpen(false);
  }, [objectApiName, recordId]);

  useEffect(() => {
    if (!objectApiName || recordId == null) return;
    void reloadRelatedData();
  }, [objectApiName, recordId, reloadRelatedData]);

  useEffect(() => {
    if (!objectApiName || recordId == null) {
      setError('Missing record information');
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [recordRes, layoutRes, fieldsRes] = await Promise.all([
          fetch(
            `${getApiUrl()}/api/objects/${encodeURIComponent(objectApiName)}/records/${encodeURIComponent(String(recordId))}`
          ),
          fetch(
            `${getApiUrl()}/api/objects/${encodeURIComponent(objectApiName)}/record-page-layout`
          ),
          fetch(
            `${getApiUrl()}/api/objects/${encodeURIComponent(objectApiName)}/fields`
          ),
        ]);

        if (!recordRes.ok) {
          const err = await recordRes.json().catch(() => ({}));
          throw new Error(err.error || 'Record not found');
        }

        const recordData = await recordRes.json();
        const layoutData = layoutRes.ok ? await layoutRes.json() : null;
        const fieldsData = fieldsRes.ok ? await fieldsRes.json() : [];

        if (cancelled) return;

        setRecord(recordData.record || null);
        setObjectLabel(recordData.object_label || objectLabelParam || objectApiName);
        setLayout(normalizeLayout(layoutData?.layout_json));
        setFieldMeta(Array.isArray(fieldsData) ? fieldsData : []);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load record');
          setRecord(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [objectApiName, recordId, objectLabelParam]);

  const handleBack = () => {
    if (isEditing) {
      cancelEdit();
    }
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }
    navigation.navigate('Records', {
      user: currentUser,
      userId: currentUser?.id || route?.params?.userId || 1,
    });
  };

  const layoutContent = useMemo(() => {
    if (!displayRecord) return null;

    const ctx = {
      record: displayRecord,
      fieldMetaByName,
      objectApiName,
      objectFieldMeta: fieldMeta,
      isUSUser,
      theme,
      styles,
      isWide,
      isEditing,
      onFieldChange: handleFieldChange,
      lookupLinks,
      onOpenRelatedRecord: openRelatedRecord,
    };
    const rows = layout?.rows || [];

    if (rows.length === 0) {
      const fields = buildFallbackDetailFields(displayRecord, fieldMetaByName);
      return (
        <SectionCard title="Details" theme={theme} styles={styles} isEditing={isEditing}>
          {fields.map(({ fieldName, label }) => (
            <EditableFieldRow
              key={fieldName}
              label={label}
              fieldName={fieldName}
              record={displayRecord}
              fieldMetaByName={fieldMetaByName}
              objectApiName={objectApiName}
              isEditing={isEditing}
              onFieldChange={handleFieldChange}
              isUSUser={isUSUser}
              theme={theme}
              styles={styles}
              isWide={isWide}
              lookupLinks={lookupLinks}
              onOpenRelatedRecord={openRelatedRecord}
            />
          ))}
        </SectionCard>
      );
    }

    return rows.map((row) => {
      const preset = LAYOUT_PRESETS[row.layoutKey] || LAYOUT_PRESETS.full;
      return (
        <View key={row.id} style={styles.layoutRow}>
          {row.columns.map((col, colIdx) => (
            <View
              key={`${row.id}-col-${colIdx}`}
              style={[styles.layoutColumn, { flex: preset.columns[colIdx] || 1 }]}
            >
              {renderColumnWidgets(col.widgets || [], ctx)}
            </View>
          ))}
        </View>
      );
    });
  }, [
    displayRecord,
    layout,
    fieldMetaByName,
    fieldMeta,
    objectApiName,
    isUSUser,
    theme,
    styles,
    isWide,
    isEditing,
    handleFieldChange,
    lookupLinks,
    openRelatedRecord,
  ]);

  if (currentUser && !hasSuperUserAccess(currentUser)) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>Super-user access required.</Text>
        <TouchableOpacity onPress={handleBack}>
          <Text style={styles.backLink}>Back to Records</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View
      style={styles.container}
      data-screen="record-detail"
      {...(Platform.OS === 'web'
        ? {
            dataSet: {
              screen: 'record-detail',
              ...(isEditing ? { recordDetailEditing: 'true' } : {}),
            },
          }
        : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />

      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} currentUser={currentUser} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <TouchableOpacity onPress={handleBack} activeOpacity={0.7}>
            <Text style={styles.breadcrumbLink}>Records</Text>
          </TouchableOpacity>
          <Text style={styles.breadcrumbSep}> › </Text>
          <Text style={styles.breadcrumb} numberOfLines={1}>
            {objectLabel || objectApiName}
          </Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <View style={styles.recordHeader}>
        <View style={styles.recordHeaderMain}>
          <View style={styles.objectPill}>
            <Text style={styles.objectPillText}>{objectLabel || objectApiName}</Text>
          </View>
          <Text style={styles.recordTitle} numberOfLines={2}>
            {loading ? 'Loading…' : recordTitle}
          </Text>
          {record?.id != null ? (
            <Text style={styles.recordIdMeta}>ID {record.id}</Text>
          ) : null}
        </View>
        <View style={styles.recordHeaderActions}>
          <TouchableOpacity style={styles.backBtn} onPress={handleBack} activeOpacity={0.85}>
            <Text style={styles.backBtnText}>← Back to list</Text>
          </TouchableOpacity>
          {!loading && !error && record && !isEditing ? (
            <TouchableOpacity
              style={styles.editBtn}
              onPress={startEdit}
              activeOpacity={0.85}
              {...(Platform.OS === 'web' ? { dataSet: { recordDetailEditBtn: 'true' } } : {})}
            >
              <Text style={styles.editBtnText}>Edit</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>

      {!loading && !error && record ? (
        <View style={styles.tabBar}>
          <TouchableOpacity
            style={[styles.tabBtn, activeTab === 'details' && styles.tabBtnActive]}
            onPress={() => setActiveTab('details')}
            activeOpacity={0.85}
            {...(Platform.OS === 'web'
              ? { dataSet: { recordDetailTab: activeTab === 'details' ? 'active' : 'inactive' } }
              : {})}
          >
            <Text
              style={[
                styles.tabBtnText,
                activeTab === 'details' && styles.tabBtnTextActive,
              ]}
            >
              Details
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tabBtn, activeTab === 'related' && styles.tabBtnActive]}
            onPress={() => setActiveTab('related')}
            activeOpacity={0.85}
            disabled={isEditing}
            {...(Platform.OS === 'web'
              ? { dataSet: { recordDetailTab: activeTab === 'related' ? 'active' : 'inactive' } }
              : {})}
          >
            <Text
              style={[
                styles.tabBtnText,
                activeTab === 'related' && styles.tabBtnTextActive,
              ]}
            >
              Related
            </Text>
          </TouchableOpacity>
        </View>
      ) : null}

      {activeTab === 'related' && selectedRelatedKeys.length > 0 ? (
        <View
          style={[
            styles.relatedBulkBar,
            {
              borderColor: theme.colors.border,
              backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#F3F2F2',
            },
          ]}
          {...(Platform.OS === 'web' ? { dataSet: { recordRelatedBulkBar: 'true' } } : {})}
        >
          <Text style={[styles.relatedBulkCount, { color: theme.colors.text }]}>
            {selectedRelatedKeys.length} selected
          </Text>
          <View style={styles.relatedBulkBtnColumn}>
            <TouchableOpacity
              style={[styles.relatedBulkBtn, styles.relatedBulkBtnDanger]}
              onPress={() => void handleDeleteSelectedRelated()}
              disabled={deletingRelated || relatedBulkEditOpen}
              {...(Platform.OS === 'web' ? { dataSet: { recordRelatedBulkDelete: 'true' } } : {})}
            >
              <Text style={styles.relatedBulkBtnDangerText}>
                {deletingRelated ? 'Deleting…' : 'Delete'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.relatedBulkBtn, styles.relatedBulkBtnEdit]}
              onPress={() => setRelatedBulkEditOpen(true)}
              disabled={deletingRelated || relatedBulkEditOpen}
              {...(Platform.OS === 'web' ? { dataSet: { recordRelatedBulkEdit: 'true' } } : {})}
            >
              <Text style={styles.relatedBulkBtnEditText}>Edit</Text>
            </TouchableOpacity>
          </View>
          <TouchableOpacity
            style={[styles.relatedBulkBtn, { borderColor: theme.colors.border }]}
            onPress={clearRelatedSelection}
            disabled={deletingRelated || relatedBulkEditOpen}
          >
            <Text style={[styles.relatedBulkBtnText, { color: theme.colors.text }]}>
              Clear selection
            </Text>
          </TouchableOpacity>
        </View>
      ) : null}

      <ScrollView
        style={[styles.contentScroll, isEditing && styles.contentScrollEditing]}
        contentContainerStyle={[
          styles.contentScrollInner,
          isEditing && styles.contentScrollInnerEditing,
        ]}
        showsVerticalScrollIndicator={false}
      >
        {loading ? (
          <View style={styles.centerState}>
            <ActivityIndicator size="large" color="#d4af37" />
          </View>
        ) : error ? (
          <View style={styles.centerState}>
            <Text style={styles.errorText}>{error}</Text>
            <TouchableOpacity onPress={handleBack}>
              <Text style={styles.backLink}>Back to Records</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <>
            {activeTab === 'details' ? (
              <View style={styles.detailCanvas}>{layoutContent}</View>
            ) : (
              <RecordRelatedLists
                relatedData={relatedData}
                loading={relatedLoading}
                error={relatedError}
                onOpenRecord={openRelatedRecord}
                theme={theme}
                enableBulkSelect
                selectedKeys={selectedRelatedKeys}
                onToggleSelect={handleToggleRelatedSelect}
              />
            )}
            {saveError ? <Text style={styles.saveErrorText}>{saveError}</Text> : null}
          </>
        )}
      </ScrollView>

      {isEditing ? (
        <View
          style={styles.footerBar}
          {...(Platform.OS === 'web' ? { dataSet: { recordDetailEditFooter: 'true' } } : {})}
        >
          <TouchableOpacity
            style={styles.cancelBtn}
            onPress={cancelEdit}
            disabled={saving}
            {...(Platform.OS === 'web' ? { dataSet: { recordDetailCancelBtn: 'true' } } : {})}
          >
            <Text style={styles.cancelBtnText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.saveBtn, (!hasDraftChanges || saving) && styles.saveBtnDisabled]}
            onPress={() => void handleSave()}
            disabled={!hasDraftChanges || saving}
            {...(Platform.OS === 'web' ? { dataSet: { recordDetailSaveBtn: 'true' } } : {})}
          >
            {saving ? (
              <ActivityIndicator size="small" color="#111" />
            ) : (
              <Text style={styles.saveBtnText}>Save</Text>
            )}
          </TouchableOpacity>
        </View>
      ) : null}

      <RecordRelatedBulkEditModal
        visible={relatedBulkEditOpen}
        items={selectedRelatedItems}
        currentUser={currentUser}
        onClose={clearRelatedSelection}
        onComplete={handleRelatedBulkEditComplete}
        theme={theme}
        isUSUser={isUSUser}
      />

      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme, isWide) => {
  const muted = theme.isDarkMode ? '#888888' : '#706e6b';
  const pageBg = theme.isDarkMode ? theme.colors.background : '#f3f3f3';
  const cardBg = theme.isDarkMode ? theme.colors.surface : '#ffffff';

  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: pageBg,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 10,
      paddingHorizontal: 20,
      backgroundColor: cardBg,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
    },
    hamburgerContainer: {
      marginRight: 15,
      backgroundColor: 'transparent',
    },
    breadcrumbContainer: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    breadcrumbLink: {
      fontSize: 14,
      color: '#d4af37',
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    breadcrumbSep: {
      fontSize: 14,
      color: muted,
      backgroundColor: 'transparent',
    },
    breadcrumb: {
      fontSize: 14,
      color: theme.colors.text,
      fontWeight: '500',
      flexShrink: 1,
      backgroundColor: 'transparent',
    },
    profileIconContainer: {
      backgroundColor: 'transparent',
    },
    recordHeader: {
      backgroundColor: cardBg,
      paddingHorizontal: 20,
      paddingBottom: 18,
      borderBottomWidth: 3,
      borderBottomColor: '#d4af37',
      flexDirection: isWide ? 'row' : 'column',
      alignItems: isWide ? 'flex-end' : 'stretch',
      justifyContent: 'space-between',
      gap: 12,
    },
    recordHeaderMain: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    objectPill: {
      alignSelf: 'flex-start',
      backgroundColor: theme.isDarkMode ? 'rgba(212,175,55,0.15)' : 'rgba(212,175,55,0.12)',
      borderRadius: 4,
      paddingHorizontal: 8,
      paddingVertical: 3,
      marginBottom: 8,
    },
    objectPillText: {
      fontSize: 11,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      color: '#d4af37',
      backgroundColor: 'transparent',
    },
    recordTitle: {
      fontSize: isWide ? 28 : 22,
      fontWeight: '300',
      color: theme.colors.text,
      lineHeight: isWide ? 34 : 28,
      backgroundColor: 'transparent',
    },
    recordIdMeta: {
      marginTop: 6,
      fontSize: 13,
      color: muted,
      backgroundColor: 'transparent',
    },
    backBtn: {
      paddingVertical: 8,
      paddingHorizontal: 12,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: 'transparent',
    },
    backBtnText: {
      fontSize: 13,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    recordHeaderActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      flexWrap: 'wrap',
      backgroundColor: 'transparent',
    },
    tabBar: {
      flexDirection: 'row',
      backgroundColor: cardBg,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
      paddingHorizontal: 20,
    },
    tabBtn: {
      paddingVertical: 12,
      paddingHorizontal: 16,
      marginRight: 8,
      borderBottomWidth: 3,
      borderBottomColor: 'transparent',
      backgroundColor: 'transparent',
    },
    tabBtnActive: {
      borderBottomColor: '#d4af37',
    },
    tabBtnText: {
      fontSize: 14,
      fontWeight: '600',
      color: muted,
      backgroundColor: 'transparent',
    },
    tabBtnTextActive: {
      color: theme.colors.text,
    },
    relatedBulkBar: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: 10,
      marginHorizontal: 20,
      marginBottom: 8,
      paddingVertical: 10,
      paddingHorizontal: 14,
      borderWidth: 1,
      borderRadius: 6,
    },
    relatedBulkBtnColumn: {
      flexDirection: 'column',
      gap: 8,
      backgroundColor: 'transparent',
    },
    relatedBulkCount: {
      fontSize: 14,
      fontWeight: '600',
      marginRight: 8,
      backgroundColor: 'transparent',
    },
    relatedBulkBtn: {
      paddingHorizontal: 14,
      paddingVertical: 8,
      borderRadius: 4,
      borderWidth: 1,
      backgroundColor: 'transparent',
    },
    relatedBulkBtnDanger: {
      backgroundColor: '#c23934',
      borderColor: '#a61d1a',
    },
    relatedBulkBtnDangerText: {
      color: '#FFFFFF',
      fontSize: 14,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    relatedBulkBtnEdit: {
      borderColor: '#d4af37',
      backgroundColor: 'transparent',
    },
    relatedBulkBtnEditText: {
      color: '#d4af37',
      fontSize: 14,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    relatedBulkBtnText: {
      fontSize: 14,
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    lookupLinkText: {
      fontSize: 14,
      fontWeight: '600',
      color: '#d4af37',
      backgroundColor: 'transparent',
      flex: isWide ? 1 : undefined,
    },
    editBtn: {
      paddingVertical: 8,
      paddingHorizontal: 16,
      borderRadius: 8,
      backgroundColor: '#d4af37',
    },
    editBtnText: {
      fontSize: 13,
      fontWeight: '700',
      color: '#111',
      backgroundColor: 'transparent',
    },
    contentScrollInnerEditing: {
      paddingBottom: 100,
    },
    contentScrollEditing: {
      ...(Platform.OS === 'web' ? { overflow: 'visible' } : {}),
    },
    fieldEditorWrap: {
      flex: isWide ? 1 : undefined,
      minWidth: 0,
      backgroundColor: 'transparent',
      overflow: 'visible',
      zIndex: 1,
    },
    fieldRowEditing: {
      overflow: 'visible',
      zIndex: 1,
    },
    footerBar: {
      flexDirection: 'row',
      gap: 12,
      paddingHorizontal: 20,
      paddingVertical: 16,
      paddingBottom: Platform.OS === 'ios' ? 28 : 16,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: theme.colors.border,
      backgroundColor: cardBg,
    },
    cancelBtn: {
      flex: 1,
      paddingVertical: 12,
      borderRadius: 8,
      alignItems: 'center',
      backgroundColor: '#999999',
    },
    cancelBtnText: {
      fontSize: 16,
      fontWeight: '600',
      color: '#FFFFFF',
      backgroundColor: 'transparent',
    },
    saveBtn: {
      flex: 1,
      paddingVertical: 12,
      borderRadius: 8,
      alignItems: 'center',
      backgroundColor: '#d4af37',
    },
    saveBtnDisabled: {
      opacity: 0.55,
    },
    saveBtnText: {
      fontSize: 16,
      fontWeight: '700',
      color: '#111',
      backgroundColor: 'transparent',
    },
    saveErrorText: {
      marginTop: 12,
      fontSize: 14,
      color: '#c62828',
      textAlign: 'center',
      backgroundColor: 'transparent',
    },
    contentScroll: {
      flex: 1,
    },
    contentScrollInner: {
      padding: 16,
      paddingBottom: 40,
      ...(isWide ? { maxWidth: 1200, alignSelf: 'center', width: '100%' } : {}),
    },
    detailCanvas: {
      gap: 16,
      backgroundColor: 'transparent',
    },
    layoutRow: {
      flexDirection: isWide ? 'row' : 'column',
      gap: 16,
      backgroundColor: 'transparent',
    },
    layoutColumn: {
      minWidth: 0,
      gap: 16,
      backgroundColor: 'transparent',
    },
    sectionCard: {
      backgroundColor: cardBg,
      borderRadius: theme.isDarkMode ? 0 : 4,
      borderWidth: theme.isDarkMode ? 1 : StyleSheet.hairlineWidth,
      borderColor: theme.colors.border,
      overflow: 'hidden',
      marginBottom: 16,
      ...(Platform.OS === 'web' && !theme.isDarkMode
        ? { boxShadow: '0 2px 2px rgba(0,0,0,0.05)' }
        : {}),
    },
    sectionCardEditing: {
      overflow: 'visible',
    },
    sectionHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
      backgroundColor: theme.isDarkMode ? '#111' : '#fafaf9',
    },
    sectionAccent: {
      width: 3,
      height: 18,
      backgroundColor: '#d4af37',
      borderRadius: 2,
      marginRight: 10,
    },
    sectionTitle: {
      fontSize: 14,
      fontWeight: '700',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    sectionBody: {
      paddingHorizontal: 16,
      paddingVertical: 8,
      backgroundColor: 'transparent',
    },
    sectionPlaceholder: {
      fontSize: 13,
      color: muted,
      fontStyle: 'italic',
      paddingVertical: 8,
      backgroundColor: 'transparent',
    },
    fieldRow: {
      paddingVertical: 10,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
      backgroundColor: 'transparent',
    },
    fieldRowWide: {
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
    fieldLabel: {
      fontSize: 12,
      fontWeight: '600',
      color: muted,
      marginBottom: isWide ? 0 : 4,
      width: isWide ? '32%' : '100%',
      paddingRight: isWide ? 16 : 0,
      backgroundColor: 'transparent',
    },
    fieldValue: {
      flex: isWide ? 1 : undefined,
      fontSize: 14,
      color: theme.colors.text,
      lineHeight: 20,
      backgroundColor: 'transparent',
    },
    textBlock: {
      backgroundColor: cardBg,
      borderRadius: theme.isDarkMode ? 0 : 4,
      borderWidth: theme.isDarkMode ? 1 : StyleSheet.hairlineWidth,
      borderColor: theme.colors.border,
      padding: 16,
      marginBottom: 16,
    },
    textBlockContent: {
      fontSize: 14,
      lineHeight: 22,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    imageBlock: {
      backgroundColor: cardBg,
      borderRadius: theme.isDarkMode ? 0 : 4,
      borderWidth: theme.isDarkMode ? 1 : StyleSheet.hairlineWidth,
      borderColor: theme.colors.border,
      padding: 12,
      marginBottom: 16,
      minHeight: 120,
      alignItems: 'center',
      justifyContent: 'center',
    },
    image: {
      width: '100%',
      height: 220,
    },
    imagePlaceholder: {
      fontSize: 13,
      color: muted,
      backgroundColor: 'transparent',
    },
    centerState: {
      paddingVertical: 48,
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    errorText: {
      fontSize: 15,
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: 12,
      backgroundColor: 'transparent',
    },
    backLink: {
      fontSize: 14,
      fontWeight: '600',
      color: '#d4af37',
      backgroundColor: 'transparent',
    },
  });
};
